import React, { useState, useEffect } from 'react';
import './Rostering.css';
import { auditFetch } from '../auditFetch';
import { usePermissions } from '../context/PermissionsContext';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

// "HH:MM" for a time input from "HH:MM:SS" or an ISO date-time string.
function toHHMM(value) {
  if (!value) return '';
  const s = String(value);
  return (s.includes('T') ? s.split('T')[1] : s).substring(0, 5);
}

export default function Rostering() {
  const { canEdit } = usePermissions();
  const readOnly = !canEdit('court-planner', 'court-planner');
  const [dates, setDates] = useState([]);
  const [courtOfficialLookup, setCourtOfficialLookup] = useState({}); // Map personId to {name, qualifications}
  const [timeOff, setTimeOff] = useState([]); // [{ personId, startAt, endAt }] still to come
  const [rosters, setRosters] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingTimes, setEditingTimes] = useState({});
  const [addForms, setAddForms] = useState({}); // date -> { personId, qualificationId, startTime, endTime } // Track which roster is being edited for times
  const [showHistorical, setShowHistorical] = useState(false); // Toggle to show last 15 days
  const [lockedDates, setLockedDates] = useState(new Set()); // Dates that are locked
  const [lockLoading, setLockLoading] = useState(null); // Date currently being locked

  // Load initial data
  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      setRosters({});
      try {
        const today = new Date();
        const datesArray = [];

        if (showHistorical) {
          // Show last 15 days (past records), newest first
          for (let i = 0; i <= 14; i++) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            const dateStr = date.toISOString().split('T')[0];
            datesArray.push({ gameDate: dateStr });
          }
        } else {
          // Show next 30 days (forward planning)
          for (let i = 0; i < 30; i++) {
            const date = new Date(today);
            date.setDate(date.getDate() + i);
            const dateStr = date.toISOString().split('T')[0];
            datesArray.push({ gameDate: dateStr });
          }
        }
        setDates(datesArray);

        // Load lock status for all visible dates
        const startD = datesArray[datesArray.length - 1].gameDate;
        const endD = datesArray[0].gameDate;
        const rangeStart = startD < endD ? startD : endD;
        const rangeEnd = startD < endD ? endD : startD;
        try {
          const lockRes = await fetch(`${API_BASE}/locked-days/range?start=${rangeStart}&end=${rangeEnd}&source=court`);
          if (lockRes.ok) {
            const lockData = await lockRes.json();
            setLockedDates(new Set(lockData));
          }
        } catch (lockErr) {
          console.error('Failed to load lock status:', lockErr.message);
        }

        // Load rosters for each date
        datesArray.forEach(d => {
          loadRosterForDate(d.gameDate);
        });

        // Load court officials with their qualifications.
        // Backend now returns one row per (person, qualification) with the
        // pay column inlined, so we build the lookup + qualification maps
        // in a single pass — no per-person follow-up fetch (was N+1, ~5s
        // for a 50-person roster, now one round-trip).
        fetch(`${API_BASE}/person-time-off`).then(r => (r.ok ? r.json() : [])).then(setTimeOff).catch(() => {});
        const officialsRes = await fetch(`${API_BASE}/court-officials`);
        if (officialsRes.ok) {
          const rows = await officialsRes.json();
          const lookup = {};                     // personId -> { name, qualifications: [{id, level, pay}] }

          for (const r of rows) {
            if (!lookup[r.id]) lookup[r.id] = { name: r.name, qualifications: [] };
            if (!r.qualificationId) continue;    // shouldn't happen now (INNER JOIN), but defensive.
            const qual = { id: r.qualificationId, level: r.qualificationLevel, pay: r.qualificationPay };
            // Avoid duplicate qual entries on the per-person list (a row
            // per qual means it's already unique on this iteration, but
            // belt-and-braces in case the response shape changes).
            if (!lookup[r.id].qualifications.some(q => q.id === qual.id)) {
              lookup[r.id].qualifications.push(qual);
            }
          }
          for (const person of Object.values(lookup)) {
            person.qualifications.sort((a, b) => a.level.localeCompare(b.level));
          }

          setCourtOfficialLookup(lookup);
        }
      } catch (err) {
        setError('Failed to load data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [showHistorical]);


  const loadRosterForDate = async (date) => {
    try {
      const response = await fetch(`${API_BASE}/court-official-rostering/${date}`);
      if (response.ok) {
        const data = await response.json();
        setRosters(prev => ({
          ...prev,
          [date]: data
        }));
      }
    } catch (err) {
      console.error(`Failed to load roster for ${date}:`, err.message);
    }
  };

  // Everyone with a court official qualification, by name.
  const officialOptions = Object.entries(courtOfficialLookup)
    .map(([id, o]) => ({ personId: Number(id), name: o.name, qualifications: o.qualifications }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Time off on a date: 'whole' when it covers the whole day, 'part' when it covers some of it (or, given the
  // shift times, the shift), else null. Times are wall-clock strings like 2026-11-03T09:00:00.
  const timeOffOn = (personId, date, startTime, endTime) => {
    const dayStart = new Date(`${date}T00:00:00`).getTime();
    const dayEnd = dayStart + 24 * 60 * 60 * 1000;
    const shiftStart = startTime && endTime ? new Date(`${date}T${startTime}:00`).getTime() : null;
    const shiftEnd = startTime && endTime ? new Date(`${date}T${endTime}:00`).getTime() : null;
    let result = null;
    for (const t of timeOff) {
      if (t.personId !== personId) continue;
      const a = new Date(t.startAt).getTime();
      const b = new Date(t.endAt).getTime();
      if (!(dayStart < b && dayEnd > a)) continue;
      if (a <= dayStart && b >= dayEnd) return 'whole';
      if (shiftStart != null && !(shiftStart < b && shiftEnd > a)) continue;
      result = 'part';
    }
    return result;
  };

  const getAddForm = (date) => addForms[date] || { personId: '', qualificationId: '', startTime: '', endTime: '' };
  const updateAddForm = (date, changes) => setAddForms(prev => ({ ...prev, [date]: { ...getAddForm(date), ...changes } }));

  // Person first; a single role on file is picked for them.
  const choosePerson = (date, personId) => {
    const quals = courtOfficialLookup[personId]?.qualifications || [];
    updateAddForm(date, { personId, qualificationId: quals.length === 1 ? String(quals[0].id) : '' });
  };

  // Same rule as My Portal and payroll: rate x hours when both times are
  // set, otherwise the flat rate.
  const shiftCost = (rate, startTime, endTime) => {
    const r = Number(rate) || 0;
    const s = toHHMM(startTime);
    const e = toHHMM(endTime);
    if (!s || !e) return r;
    const [sh, sm] = s.split(':').map(Number);
    const [eh, em] = e.split(':').map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins <= 0) mins += 24 * 60;
    return r * (mins / 60);
  };

  // A person can work more than one shift a day, so every add is a new row.
  const handleAddToRoster = async (date) => {
    const form = getAddForm(date);
    if (!form.personId || !form.qualificationId) return;
    setError('');
    if (timeOffOn(Number(form.personId), date, form.startTime, form.endTime)) {
      setError(`${courtOfficialLookup[form.personId]?.name || 'This person'} has time off then, so cannot be rostered.`);
      return;
    }
    try {
      const response = await auditFetch(`${API_BASE}/court-official-rostering/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assignmentDate: date,
          courtOfficialQualificationId: parseInt(form.qualificationId, 10),
          startTime: form.startTime && form.endTime ? form.startTime : null,
          endTime: form.startTime && form.endTime ? form.endTime : null,
          courtOfficialIds: [parseInt(form.personId, 10)]
        })
      });
      if (response.ok) {
        setAddForms(prev => {
          const next = { ...prev };
          delete next[date];
          return next;
        });
        await loadRosterForDate(date);
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.error || 'Failed to add court official');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleRemoveOfficial = async (rosterId, date) => {
    if (!window.confirm('Remove this court official from the roster?')) return;

    try {
      const response = await auditFetch(`${API_BASE}/court-official-rostering/${rosterId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Court official removed from roster!');
        await loadRosterForDate(date);
      } else {
        setError('Failed to remove court official');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleAttendanceToggle = async (rosterId, attended, date) => {
    try {
      const response = await auditFetch(`${API_BASE}/court-official-rostering/${rosterId}/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attended: !attended })
      });

      if (response.ok) {
        await loadRosterForDate(date);
      } else {
        setError('Failed to update attendance');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // Inline start/end times: saved once both are filled in.
  const handleTimeChange = async (roster, date, field, value) => {
    const draft = { ...(editingTimes[roster.id] || { startTime: toHHMM(roster.startTime), endTime: toHHMM(roster.endTime) }), [field]: value };
    setEditingTimes(prev => ({ ...prev, [roster.id]: draft }));
    if (!draft.startTime || !draft.endTime) return;
    try {
      const response = await auditFetch(`${API_BASE}/court-official-rostering/${roster.id}/times`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ startTime: draft.startTime, endTime: draft.endTime })
      });
      if (response.ok) {
        await loadRosterForDate(date);
        setEditingTimes(prev => {
          const updated = { ...prev };
          delete updated[roster.id];
          return updated;
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.error || 'Failed to save times');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const officialLabel = (roster) => {
    const official = courtOfficialLookup[roster.courtOfficialId];
    if (!official) return roster.courtOfficialName || 'Unknown';
    const qual = official.qualifications.find(q => q.id === roster.courtOfficialQualificationId);
    return qual ? `${official.name} - ${qual.level}` : official.name;
  };

  const handleLockDay = async (date) => {
    if (!window.confirm(`Lock ${date}? This will prevent any changes to court official assignments for this day.`)) return;
    setLockLoading(date);
    try {
      const response = await auditFetch(`${API_BASE}/locked-days`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, source: 'court' })
      });
      if (response.ok) {
        setLockedDates(prev => new Set([...prev, date]));
        setSuccess(`Day ${date} locked successfully`);
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.error || 'Failed to lock day');
      }
    } catch (err) {
      setError('Error locking day: ' + err.message);
    } finally {
      setLockLoading(null);
    }
  };

  return (
    <div className="rostering court-compact">
      <label className="court-history-toggle">
        <input
          type="checkbox"
          checked={showHistorical}
          onChange={(e) => setShowHistorical(e.target.checked)}
        />
        {showHistorical ? 'Showing last 15 days' : 'Showing next 30 days'} <span>(tick to {showHistorical ? 'show upcoming' : 'show the last 15 days'})</span>
      </label>

      {error && <div className="error-message" onClick={() => setError('')}>{error}</div>}
      {success && <div className="success-message" onClick={() => setSuccess('')}>{success}</div>}
      {loading && <div className="loading">Loading roster data...</div>}

      <div className="court-day-grid">
        {dates.map(({ gameDate: date }) => {
          const dateStr = new Date(`${date}T00:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
          const dayRosters = rosters[date] || [];
          const isDateLocked = lockedDates.has(date);
          const editable = !readOnly && !isDateLocked;
          const form = getAddForm(date);
          const chosen = form.personId ? courtOfficialLookup[form.personId] : null;
          const chosenQual = chosen && form.qualificationId ? chosen.qualifications.find(q => String(q.id) === String(form.qualificationId)) : null;
          const rowCost = (roster) => {
            const draft = editingTimes[roster.id];
            return shiftCost(roster.pay, draft ? draft.startTime : roster.startTime, draft ? draft.endTime : roster.endTime);
          };
          const dayPay = dayRosters.reduce((sum, r) => sum + (r.response === 'rejected' ? 0 : rowCost(r)), 0);

          return (
            <div key={date} className={`court-day-tile${isDateLocked ? ' locked' : ''}`}>
              <div className="court-day-header">
                <span className="court-day-title">{dateStr}</span>
                <span className="court-day-right">
                  <span className="court-day-cost" title="Day cost">${dayPay.toFixed(2)}</span>
                  {!readOnly && (isDateLocked
                    ? <span className="court-locked-tag">Locked</span>
                    : (
                      <button className="court-lock-btn" disabled={lockLoading === date} onClick={() => handleLockDay(date)}>
                        {lockLoading === date ? 'Locking…' : 'Lock'}
                      </button>
                    ))}
                </span>
              </div>

              {dayRosters.map(roster => {
                const draft = editingTimes[roster.id];
                const start = draft ? draft.startTime : toHHMM(roster.startTime);
                const end = draft ? draft.endTime : toHHMM(roster.endTime);
                const pill = roster.response === 'accepted' ? ' accepted' : roster.response === 'rejected' ? ' rejected' : '';
                return (
                  <div key={roster.id} className="court-row">
                    <span className={`court-name${pill}`} title={roster.response === 'rejected' ? 'Rejected in My Portal' : roster.response === 'accepted' ? 'Accepted in My Portal' : ''}>
                      {officialLabel(roster)}{roster.response === 'rejected' ? ' (Rejected)' : ''}
                    </span>
                    <input
                      type="time"
                      className="court-time"
                      value={start || ''}
                      disabled={!editable}
                      onChange={(e) => handleTimeChange(roster, date, 'startTime', e.target.value)}
                      title="Start time"
                    />
                    <input
                      type="time"
                      className="court-time"
                      value={end || ''}
                      disabled={!editable}
                      onChange={(e) => handleTimeChange(roster, date, 'endTime', e.target.value)}
                      title="End time"
                    />
                    <span className="court-cost">{roster.response === 'rejected' ? '–' : `$${rowCost(roster).toFixed(2)}`}</span>
                    <input
                      type="checkbox"
                      className="court-attended"
                      checked={roster.attended || false}
                      disabled={!editable}
                      onChange={() => handleAttendanceToggle(roster.id, roster.attended, date)}
                      title="Attended"
                    />
                    {editable && (
                      <button className="court-remove" onClick={() => handleRemoveOfficial(roster.id, date)} title="Remove">&#10005;</button>
                    )}
                  </div>
                );
              })}

              {editable && (
                <div className="court-row court-add-row">
                  <select className="court-add-select" value={form.personId} onChange={(e) => choosePerson(date, e.target.value)}>
                    <option value="">+ Add court official</option>
                    {officialOptions.map(o => {
                      const off = timeOffOn(o.personId, date, form.startTime, form.endTime);
                      return (
                        <option key={o.personId} value={o.personId} disabled={off === 'whole'}>
                          {o.name}{off === 'whole' ? ' (Time off)' : off === 'part' ? ' (Time off, part of the day)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  <select
                    className="court-add-select court-role-select"
                    value={form.qualificationId}
                    disabled={!chosen}
                    onChange={(e) => updateAddForm(date, { qualificationId: e.target.value })}
                  >
                    <option value="">{chosen ? 'Role…' : 'Role'}</option>
                    {(chosen ? chosen.qualifications : []).map(q => (
                      <option key={q.id} value={q.id}>{q.level} (${q.pay}/hr)</option>
                    ))}
                  </select>
                  <input type="time" className="court-time" value={form.startTime} disabled={!chosen}
                    onChange={(e) => updateAddForm(date, { startTime: e.target.value })} title="Start time" />
                  <input type="time" className="court-time" value={form.endTime} disabled={!chosen}
                    onChange={(e) => updateAddForm(date, { endTime: e.target.value })} title="End time" />
                  <span className="court-cost">{chosenQual ? `$${shiftCost(chosenQual.pay, form.startTime, form.endTime).toFixed(2)}` : ''}</span>
                  <button className="court-add-btn" disabled={!chosenQual} onClick={() => handleAddToRoster(date)}>Add</button>
                </div>
              )}
              {!editable && dayRosters.length === 0 && <div className="court-empty">No court officials</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
